import {MarkdownViewer} from '@github-ui/markdown-viewer'
import {editBlobPath} from '@github-ui/paths'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {sanitize} from '@github-ui/sanitize'
import {ChevronDownIcon, CopilotIcon, KebabHorizontalIcon, PencilIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'
import type {ReactNode} from 'react'
import {Suspense, useEffect, useId, useMemo, useState} from 'react'
import {graphql, useLazyLoadQuery} from 'react-relay'

import type {IssuePlanBodyQuery as IssuePlanBodyQueryType} from './__generated__/IssuePlanBodyQuery.graphql'
import styles from './IssuePlanBody.module.css'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssuePlanBodyQuery = graphql`
  query IssuePlanBodyQuery($id: ID!) {
    node(id: $id) {
      ... on Issue {
        plan {
          body
          bodyHTML
          filePath
          branch
        }
        repository {
          nameWithOwner
        }
      }
    }
  }
`

export const PLAN_BODY_POLL_INTERVAL_MS = 5000

export type PlanData = {
  planBody: string
  editUrl?: string
}

type IssuePlanBodyProps = {
  issueId: string
  renderAction?: (data: PlanData) => ReactNode
}

function IssuePlanBodyContent({issueId, renderAction}: IssuePlanBodyProps) {
  const [fetchKey, setFetchKey] = useState(0)

  useEffect(() => {
    const id = setInterval(() => {
      if (!document.hidden) {
        setFetchKey(k => k + 1)
      }
    }, PLAN_BODY_POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [])

  const data = useLazyLoadQuery<IssuePlanBodyQueryType>(
    IssuePlanBodyQuery,
    {id: issueId},
    fetchKey > 0 ? {fetchKey, fetchPolicy: 'store-and-network'} : undefined,
  )
  const plan = data.node?.plan
  const planBody = plan?.body
  const planBodyHTML = plan?.bodyHTML
  const planFilePath = plan?.filePath
  const planBranch = plan?.branch

  const nameWithOwner = data.node?.repository?.nameWithOwner

  if (!planBodyHTML || !planBody) {
    return null
  }

  const [owner, ...repoSegments] = nameWithOwner?.split('/') ?? []
  const repo = repoSegments.join('/')
  const editUrl =
    planFilePath && planBranch && owner && repo
      ? editBlobPath({
          owner,
          repo,
          commitish: planBranch,
          filePath: planFilePath,
        })
      : undefined

  const renderedAction = renderAction?.({planBody, editUrl})

  return <IssuePlanBodyView planBodyHTML={planBodyHTML} editUrl={editUrl} renderedAction={renderedAction} />
}

type IssuePlanBodyViewProps = {
  planBodyHTML: string
  editUrl?: string
  renderedAction?: ReactNode
}

function PlanActionMenu({editUrl}: {editUrl: string}) {
  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButton size="small" icon={KebabHorizontalIcon} variant="invisible" aria-label="Plan actions" />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay>
        <ActionList>
          <ActionList.LinkItem href={editUrl}>
            <ActionList.LeadingVisual>
              <PencilIcon />
            </ActionList.LeadingVisual>
            Edit
          </ActionList.LinkItem>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function IssuePlanBodyView({planBodyHTML, editUrl, renderedAction}: IssuePlanBodyViewProps) {
  const sanitizedHTML = useMemo(() => sanitize(planBodyHTML), [planBodyHTML])
  const [isExpanded, setIsExpanded] = useState(true)
  const contentId = useId()

  return (
    <div className={styles.issuePlanBody} data-testid="issue-plan-body">
      <div className={styles.header}>
        <button
          type="button"
          className={styles.summaryButton}
          data-testid="issue-plan-summary"
          aria-expanded={isExpanded}
          aria-controls={contentId}
          onClick={() => setIsExpanded(previous => !previous)}
        >
          <span className={styles.summaryMain}>
            <span className={isExpanded ? styles.chevron : styles.chevronCollapsed}>
              <ChevronDownIcon size={16} />
            </span>
            <span className={styles.icon}>
              <CopilotIcon size={16} />
            </span>
            <span className={styles.title}>Implementation plan</span>
            <span className={styles.versionBadge}>v1</span>
          </span>
        </button>
        {renderedAction || editUrl ? (
          <div className={styles.summaryAction} data-testid="plan-actions">
            {renderedAction}
            {editUrl ? <PlanActionMenu editUrl={editUrl} /> : null}
          </div>
        ) : null}
      </div>

      {isExpanded ? (
        <div id={contentId} className={styles.content}>
          <MarkdownViewer verifiedHTML={sanitizedHTML} />
        </div>
      ) : null}
    </div>
  )
}

export function IssuePlanBody({issueId, renderAction}: IssuePlanBodyProps) {
  return (
    <ErrorBoundary boundaryName="IssuePlanBody" fallback={null} critical={false}>
      <Suspense fallback={null}>
        <IssuePlanBodyContent issueId={issueId} renderAction={renderAction} />
      </Suspense>
    </ErrorBoundary>
  )
}
